import { ExternalEvidence } from './ExternalEvidence';
import { Card, ActorName } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { api } from '../../api/hooks';
import { useCrumbs } from '../../layout/crumbs';
import { ErrorAlert, QueryView } from '../../ui/feedback';
import { usePageTitle } from '../../ui/misc';
import { Approvals } from './detail/Approvals';
import { Attempts } from './detail/Attempts';
import { Candidate } from './detail/Candidate';
import { ReleaseDialogs } from './detail/Dialogs';
import { ReleaseHeader } from './detail/Header';
import { History } from './detail/History';
import { MergeGateCard } from './detail/MergeGate';
import { TicketReadiness } from './detail/Readiness';
import { Rollbacks } from './detail/Rollbacks';
import { ReleaseStatus } from './detail/Status';
import { useReleaseActions } from './detail/useReleaseActions';
import type { ReleaseDetail } from './types';
import { UnlinkedRunsCard } from './UnlinkedRuns';

/** WF-10–15: shared release loading, routing and section composition. */
export function ReleaseDetailPage() {
  const { key, releaseId } = useParams();
  const q = useQuery({
    queryKey: ['release', key, releaseId],
    queryFn: () => api.get<ReleaseDetail>(`/projects/${key}/releases/${releaseId}`),
  });
  usePageTitle(q.data ? `${q.data.project.name} · Release ${q.data.displayId}` : null);
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Releases', to: `/projects/${key}/releases` },
    { label: releaseId ?? '' },
  ]);

  const actions = useReleaseActions(key, releaseId, q.data);
  return (
    <div className="page">
      <QueryView query={q}>
        {(r) => (
          <>
            <ReleaseHeader r={r} actions={actions} />
            <ErrorAlert error={actions.refresh.error} />
            <ReleaseStatus r={r} base={actions.base} />
            <div className="split">
              <div className="stack">
                {r.mergeGate && <MergeGateCard gate={r.mergeGate} projectKey={key!} />}
                {r.candidate?.binding && <Candidate r={r} />}
                <TicketReadiness r={r} projectKey={key!} />
                <UnlinkedRunsCard runs={r.unmatchedRuns} />
                {r.externalDeliveries?.map((d) => (
                  <Card
                    key={d.id}
                    title="Accepted existing deployment"
                    subtitle="Verified by admin review; recorded separately from execution attempts."
                  >
                    <p>
                      Recorded <Timestamp value={d.recordedAt} /> by review of{' '}
                      <ActorName actor={d.submittedBy} />
                      's submission. Accepted by <ActorName actor={d.acceptedBy} />. Closed {d.closedTickets}{' '}
                      ticket(s).
                    </p>
                    <ExternalEvidence evidence={d.evidence} />
                  </Card>
                ))}
                <Attempts r={r} base={actions.base} />
              </div>
              <div className="stack">
                <Approvals r={r} />
                <Rollbacks r={r} base={actions.base} />
                <History r={r} />
              </div>
            </div>
            <ReleaseDialogs r={r} actions={actions} />
          </>
        )}
      </QueryView>
    </div>
  );
}
